import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { debounce } from 'underscore';

import { useTranslate } from '../../../i18n/useTranslationModules';
import { getQueryParams } from '../../../utils/utils';
import Icon, { ICONS } from '../icon';

import './search.scss';

interface SearchProps {
	onSearch?: (searchText: string) => void;
	onClear: () => void;
	placeholder?: string;
	onDebounce?: (searchText: string) => void;
	searchOnDebounce?: boolean;
	controlledValue?: string;
	isControlled?: boolean;
	className?: string;
	onChange?: (searchText: string) => void;
	searchOnEmpty?: boolean;
	style?: React.CSSProperties;
	minLength?: number;
	autoFocus?: boolean
}

export default function Search({
	onSearch = () => null,
	onClear,
	placeholder,
	onDebounce,
	searchOnDebounce,
	controlledValue = '',
	isControlled = false,
	className,
	onChange,
	searchOnEmpty = false,
	style = {},
	minLength = 0,
	autoFocus = false
}: SearchProps): JSX.Element {
	const [search, setSearch] = useState(controlledValue);
	const searchRef = useRef<HTMLInputElement>(null);
	const { t } = useTranslate("homepage");

	const location = useLocation();
	const queryParams = getQueryParams(location.search);

	useEffect(() => {
		if (queryParams?.q) {
			setSearch(decodeURIComponent(queryParams.q));
		}
	}, [queryParams.q]);

	// if isControlled, meaning if the value comes from the parent component
	// then set the search value to be that value
	useEffect(() => {
		if (isControlled) {
			setSearch(controlledValue);
		}
	}, [isControlled, controlledValue]);

	const canSubmit = () => search.length >= minLength;

	const debounced = useRef(debounce((value: string) => {
		onDebounce && onDebounce(value);
		if (searchOnDebounce && canSubmit()) onSearch(value);
	}, 300)).current;

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setSearch(e.target.value);

		if (onDebounce) {
			debounced(e.target.value);
		}

		if (onChange && canSubmit()) {
			onChange(e.target.value);
		}

		// if value is empty and searchOnEmpty automatically reset
		if (search.length && !e.target.value.length && searchOnEmpty) {
			handleClearSearch();
		}
	};

	const handleClearSearch = () => {
		setSearch('');
		// searchRef.current?.blur();
		searchRef.current?.focus();
		onClear();
	};
	const handleKeyPressSearch = ({ key }: any) => {
		switch (key) {
			case 'Escape':
				handleClearSearch();
				break;
			case 'Enter':
				if (canSubmit()) {
					onSearch(search);
				}
				break;
			default:
				return;
		}
	};
	return (
		<div className={classNames('search-input', className)} style={style}>
			<button
				onClick={() => {
					if (canSubmit()) {
						onSearch(search);
					}
				}}
				className="no-style clear search-input-search-btn"
			>
				<Icon
					name={ICONS.SEARCH}
					color={'gray'}
					size={12}
				/>
			</button>
			<input
				type="text"
				placeholder={placeholder ? placeholder : t("Search")}
				ref={searchRef}
				onKeyUp={handleKeyPressSearch}
				value={search}
				onChange={handleChange}
				autoFocus={autoFocus}
			/>
			{!search ? (
				<div className="search-input-close-placeholder" />
			) : (
				<button
					onClick={handleClearSearch}
					className='no-style clear search-input-close-btn'
				>
					<Icon name={ICONS.CLOSE} color="gray" size={12} />
				</button>
			)}
		</div>
	);
}
